import { useRef, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Camera, Trash2, Loader2, Check, X, ZoomIn } from "lucide-react";
import api from "../lib/api";
import Avatar from "./Avatar";
import Modal from "./Modal";

/**
 * AvatarUploader — pick image → drag-to-crop modal → canvas downscale/compress → upload.
 *
 * Props:
 *   user: { id, name, email, avatar_url }
 *   targetUid: admin override — POST /users/{uid}/avatar instead of /users/me/avatar
 *   onChange(newAvatarUrl|null)
 *   size: Avatar preview size
 *
 * Client-side compression:
 *   Cropped output is drawn onto a 512×512 canvas and encoded as JPEG quality
 *   0.9 via canvas.toBlob. A 10 MB phone selfie typically lands under 80 KB
 *   over the wire — 125× bandwidth cut for anyone tethering on a cell tower.
 *   The backend still does its own 256×256 re-encode so this is defence-in-
 *   depth rather than the canonical pipeline.
 */

const UPLOAD_MAX_PX = 512; // what the client compresses to
const UPLOAD_JPEG_QUALITY = 0.9;

export default function AvatarUploader({ user, targetUid = null, onChange, size = "lg" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Cropper state — populated when user picks a file
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);

  const endpoint = targetUid ? `/users/${targetUid}/avatar` : "/users/me/avatar";

  const pick = () => fileRef.current?.click();

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const openCropper = (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      setErr("Pick an image file (JPG / PNG / WebP).");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setErr("Image must be ≤ 20 MB.");
      return;
    }
    setErr("");
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(f);
  };

  const cancelCrop = () => {
    setImageSrc(null);
    setCroppedPixels(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmCrop = async () => {
    if (!imageSrc || !croppedPixels) return;
    setBusy(true);
    setErr("");
    try {
      const blob = await cropAndCompress(imageSrc, croppedPixels);
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.avatar_url || null);
      setImageSrc(null);
      setCroppedPixels(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.delete(endpoint);
      onChange?.(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Remove failed.");
    } finally {
      setBusy(false);
    }
  };

  const hasPic = !!user?.avatar_url;

  return (
    <div className="flex items-start gap-4" data-testid="avatar-uploader">
      <div className="relative">
        <Avatar user={user} size={size} />
        {busy && (
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-[#647087]">Profile picture</div>
        <div className="text-xs text-[#a0aabf] mt-1 leading-relaxed">
          PNG, JPG or WebP. Crop to a square on the next step — we'll compress to 512×512 before upload.
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => openCropper(e.target.files?.[0])}
            data-testid="avatar-file-input"
          />
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="avatar-upload-btn"
          >
            <Camera className="w-3.5 h-3.5" /> {hasPic ? "Replace" : "Upload"}
          </button>
          {hasPic && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:text-rose-200"
              data-testid="avatar-remove-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
        {err && (
          <div className="text-xs text-rose-300 mt-2" data-testid="avatar-error">
            {err}
          </div>
        )}
      </div>

      {imageSrc && (
        <Modal
          open
          onClose={cancelCrop}
          title="Crop your photo"
          testId="avatar-crop-modal"
          maxWidth="max-w-xl"
          footer={
            <div className="flex items-center justify-between gap-2 w-full flex-wrap">
              <div className="flex items-center gap-2 text-xs text-[#a0aabf] flex-1 min-w-[160px]">
                <ZoomIn className="w-3.5 h-3.5" />
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="0.05"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                  data-testid="avatar-crop-zoom"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={cancelCrop}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid="avatar-crop-cancel"
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmCrop}
                  disabled={busy || !croppedPixels}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="avatar-crop-confirm"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  {busy ? "Uploading…" : "Use this photo"}
                </button>
              </div>
            </div>
          }
        >
          <div className="relative w-full h-[min(420px,60vh)] bg-black rounded-md overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="text-[11px] text-[#647087] mt-2 leading-relaxed">
            Drag to reposition. Pinch / scroll / use the slider to zoom. We crop to a square and downscale to 512×512 before upload.
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Load an image element from a data URL. */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Given the source image + pixel-space crop rect from react-easy-crop,
 *  draw the crop into a UPLOAD_MAX_PX canvas and return a JPEG Blob. */
async function cropAndCompress(imageSrc, pixels) {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = UPLOAD_MAX_PX;
  canvas.height = UPLOAD_MAX_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    pixels.x,
    pixels.y,
    pixels.width,
    pixels.height,
    0,
    0,
    UPLOAD_MAX_PX,
    UPLOAD_MAX_PX,
  );
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas encode failed"))),
      "image/jpeg",
      UPLOAD_JPEG_QUALITY,
    );
  });
}
